import { ConsumerPortalConfig } from '../../models/consumer-portal-config';
import { AppFacadeService } from '../../services/app-facade.service';
import { NotificationService } from '../../services/notification.service';
import { ModalService } from '../../services/modal.service';
import { debounceTime, delay, tap, distinctUntilChanged, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
    ApplyColumnStateParams,
    ColDef,
    ColumnApi,
    CsvExportParams,
    GetRowIdFunc,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    IsFullWidthRowParams,
    IsRowSelectable,
    ProcessCellForExportParams,
    RowNode,
    ValueGetterParams
} from 'ag-grid-community';
import { ConfigurationService } from '../../services/configuration.service';
import { ItemOutService } from '../../services/item-out.service';
import { clearItemsOut, updateRow } from '../../store/domains/items-out-actions/items-out-action.actions';
import { getAllItemsOut, getNoMoreRecordSelector } from '../../store/domains/itemsout/itemsout.selectors';
import { TranslateService } from '@ngx-translate/core';
import { formatDateType } from '../../types';
import { AgGridService } from '../../services/ag-grid.service';
import { Filter, FilterOutput } from '../../shared/models/filters.model';
import { ItemOutCP } from '../../models/item-out-model';
import { LineItemStatus } from '../../enums/line-item-status.enum';
import { ItemsOutFullWidthRowComponent } from '../items-out-full-width-row/items-out-full-width-row.component';
import { ItemsOutDetailRendererComponent } from './renderer/items-out-detail-renderer/items-out-detail-renderer.component';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { RequestCellRendererComponent } from './renderer/request-cell-renderer';
import { ItemsOutActionsMenuComponent } from './renderer/items-out-actions-menu.component';
import { CallOffRequest } from '../../models/calloff-request.model';
import { RequestActionService } from '../../services/request-action.service';
import { RequestActionsToLoad } from '../../enums/request-actions.enum';
import { ReturnRequestType } from '../../enums/return-request-type.enum';
import { DepotTimezoneDatePipe } from '../../pipes/depot-timezone-date.pipe';
import { AppMediatorService } from '../../services/app-mediator.service';
import { ContractDetailRendererComponent } from './renderer/items-out-contract-detail-renderer/contract-detail-renderer.component';
import { SubComponentsToLoad } from '../../enums/components-to-load.enum';
import { UserAccountFeature } from '../../enums/user-account-feature.enum';
import { OFF_RENT_MAX_SELECTION_ALLOWED } from '../../constants/default.const';
@Component({
    standalone: false,
    selector: 'por-items-out',
    templateUrl: './items-out.component.html',
    styleUrls: ['./items-out.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemsOutComponent implements OnInit, OnDestroy {
    readonly appFacadeService = inject(AppFacadeService);
    itemOutService = inject(ItemOutService);
    private readonly store = inject(Store);
    private readonly notification = inject(NotificationService);
    private readonly configService = inject(ConfigurationService);
    private readonly datePipe = inject(DepotTimezoneDatePipe);
    private readonly translateService = inject(TranslateService);
    private readonly modal = inject(ModalService);
    private readonly agGrid = inject(AgGridService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly featureService = inject(FeatureToggleService);
    private readonly requestActionService = inject(RequestActionService);
    private readonly cdr = inject(ChangeDetectorRef);

    currentWidth = window.innerWidth;
    public context: unknown;
    constructor() {
        const store = this.store;

        store.dispatch(clearItemsOut());
        this.context = {
            componentParent: this
        };
    }
    public fullWidthCellRenderer = ItemsOutFullWidthRowComponent;
    public isSearchSupported = this.featureService.isAvailable('textSearchMultipleFilters');

    // Off-Rent needs BOTH the per-user call-off permission AND the org-level call-off ability.
    // porFeatureAvailability is any-of, so the render gate cannot express the AND on its own.
    get isOffRentAvailable(): boolean {
        return this.featureService.isAvailable(UserAccountFeature.RequestCallOff) && this.featureService.isAvailable('callOffAbility');
    }

    public getScreenWidth: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);
    itemsOut$: Observable<ItemOutCP[]> = this.store.select(getAllItemsOut);
    headerHeight = 35;
    rowHeight = 65;
    uiUrl: string | undefined;
    /* Single Filter Can be Enum: "Reserved" "Out" "Off Rent" "Returned" "Sold" */
    /* Sentinel filter value that shows items of every status (no status filter). */
    readonly allFilterValue = 'All';
    singleFilters: Filter[] = [
        {
            label: this.translateService.instant('Booked'),
            value: LineItemStatus.Reserved
        },
        {
            label: this.translateService.instant('OnRent'),
            value: LineItemStatus.Out
        },
        {
            label: this.translateService.instant('PastYear'),
            value: LineItemStatus.OffRent
        },
        {
            label: this.translateService.instant('AllItems'),
            value: this.allFilterValue
        }
    ];
    singleFiltersDefaultValue: LineItemStatus = LineItemStatus.Out;
    filters: FilterOutput = {
        singleFilter: this.singleFiltersDefaultValue,
        multifilters: [],
        search: '',
        dates: {
            startDate: undefined,
            endDate: undefined
        }
    };
    hasMoreRecord$: Observable<boolean> = this.store.select(getNoMoreRecordSelector);
    @Input() customerId!: string;
    private readonly subscriptions: Subscription[] = [];
    gridApi!: GridApi;
    columnApi!: ColumnApi;
    public columnDefs: ColDef[] = [];
    inputs: ConsumerPortalConfig | undefined;
    isFullWidth = false;
    gridReady$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    gridReadyObservable$: Observable<boolean> = this.gridReady$.pipe(delay(1000));
    lineItemStatusFilter: string | string[] | undefined;
    public filterComponentProps: {
        searchPlaceHolder: string;
        dateLabel: string;
        startDateLabel: string;
    } = {
        searchPlaceHolder: this.translateService.instant('searchItems'),
        dateLabel: this.translateService.instant('selectDueDateRange'),
        startDateLabel: 'itemsoutStartDateFilter'
    };

    @Input() config: string | undefined;
    @Input() width: number | undefined;
    @Input() height: string | undefined;
    @Output() readonly elementLoaded = new EventEmitter<boolean>();
    @Output() readonly appOutput: EventEmitter<ItemOutCP[]> = new EventEmitter<ItemOutCP[]>();

    public defaultColDef: ColDef = {
        sortable: true,
        resizable: true,
        suppressMovable: true,
        wrapText: false,
        headerComponentParams: {
            template: this.getHeaderTemplate()
        },
        suppressMenu: true
    };

    public rowSelection: 'single' | 'multiple' = 'multiple';
    selectedRows: ItemOutCP[] = [];
    /* Items shown/submitted in the request modal. Decoupled from the bulk selection so per-item
       actions (row kebab / mobile "Requests") don't affect the Off-Rent button's enabled state. */
    modalItems: ItemOutCP[] = [];
    selectionDisabled = true;
    totalRowsSelected = 0;
    /* View the rental request modal should open directly on, bypassing the middle step. */
    requestInitialView: RequestActionsToLoad = RequestActionsToLoad.RequestReturnView;
    /* Controls creation of the request modal content so it is rebuilt fresh (current items + view) on every open. */
    isRequestModalOpen = false;
    /* True only when the modal was opened from the bulk selection (Off-Rent). Per-item entry points
       (row kebab / mobile "Requests") must leave an existing selection untouched on close. */
    private openedFromSelection = false;
    /* Max items a single off-rent request may carry (ADO-108924 AC), enforced on both the grid
       selection and the mobile card checkboxes, and again by the API's CallOffRequest schema. */
    readonly maxItemsSelectable = OFF_RENT_MAX_SELECTION_ALLOWED;
    /* True while the cap is deselecting surplus rows. node.setSelected() re-fires selectionChanged
       (ag-grid 28 has no batch setNodesSelected), so without this the user would get one
       notification per deselected row. */
    private enforcingSelectionCap = false;

    public isFullWidthRow: (params: IsFullWidthRowParams) => boolean = () => {
        return this.isFullWidth;
    };

    formatDate = (params: formatDateType) => {
        // Extract depotId from item data for depot-specific timezone
        const depotId = params.data?.depotId;

        // Get date only (MM/DD/YYYY format)
        const dateOnly$ = this.datePipe.transform(params.value, 'dateOnly', depotId);
        let dateOnlyStr = '';
        dateOnly$.subscribe(d => (dateOnlyStr = d)).unsubscribe();

        // Get full format to extract timezone abbreviation
        const full$ = this.datePipe.transform(params.value, 'full', depotId);
        let fullStr = '';
        full$.subscribe(d => (fullStr = d)).unsubscribe();

        // Extract timezone abbreviation from full format (e.g., "EST", "CST", "MST")
        const timezoneMatch = fullStr.match(/\s([A-Z]{3,4})$/);
        const timezone = timezoneMatch ? timezoneMatch[1] : '';

        // Combine: "12/2/2025 CST"
        const formattedDate = timezone ? `${dateOnlyStr} ${timezone}` : dateOnlyStr;

        return `<span class="theme-table-row">${formattedDate}</span>`;
    };

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.columnApi = params.columnApi;
        this.gridApi.setDomLayout('autoHeight');
        this.autoSizeAll();
        this.agGrid.setHorizontalScrollBar();
        const columnState: ApplyColumnStateParams = {
            state: [
                {
                    colId: 'dueDate',
                    sort: 'desc'
                }
            ]
        };
        this.columnApi.applyColumnState(columnState);
        document.querySelectorAll('.ag-header-container .ag-checkbox-input').forEach(e => {
            e.setAttribute('data-testid', 'por-cp-header-checkbox');
        });
    }
    onRowDataUpdated() {
        document.querySelectorAll('.ag-cell-wrapper[aria-hidden="true"]').forEach(el => el.removeAttribute('aria-hidden'));
        document.querySelectorAll('.ag-checkbox[aria-hidden="true"]').forEach(el => el.removeAttribute('aria-hidden'));
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    // Need to check if it exceeds the breakpoint, then need to check responsive grid
    @HostListener('window:resize', ['$event'])
    onWindowResize() {
        if (this.currentWidth >= 1024 && window.innerWidth <= 1024) {
            this.getScreenWidth.next(window.innerWidth);
        } else if (this.currentWidth <= 1024 && window.innerWidth >= 1024) {
            this.getScreenWidth.next(window.innerWidth);
        }
        this.currentWidth = window.innerWidth;
    }

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }

        this.subscriptions.push(
            this.getScreenWidth.pipe(debounceTime(1000)).subscribe((width: number) => {
                this.setResponsiveGrid(width);
            })
        );

        // Rebuild the grid columns once the user-accounts/roles API has resolved, so permission-gated
        // columns reflect the user's roles instead of the default (fail-closed) state (ADO-114494).
        this.subscriptions.push(
            this.appFacadeService
                .isAccountFeaturesLoaded()
                .pipe(filter(Boolean))
                .subscribe(() => {
                    this.setResponsiveGrid(this.currentWidth);
                })
        );
        this.configService.setMatIconFontFamily();
        if (this.config && !isEmpty(this.config)) {
            this.inputs = JSON.parse(this.config);
        }

        if (!this.customerId) {
            this.notification.error(this.translateService.instant('customerIdNotConfigured'));
            this.elementLoaded.emit(false);
            return;
        }
        this.itemOutService.getItemsOut({
            customerId: this.customerId,
            status: this.singleFiltersDefaultValue
        });

        this.elementLoaded.emit(true);
        this.subscriptions.push(
            this.itemsOut$.subscribe(itemsOut => {
                if (!isEmpty(itemsOut)) {
                    this.appOutput.emit(itemsOut);
                    this.columnApi?.setColumnVisible(
                        'info',
                        itemsOut.some(item => !isEmpty(item.info))
                    );
                    this.autoSizeAll();
                }
            })
        );

        // checking window size
        this.subscriptions.push(
            this.breakpointObserver
                .observe(['(max-width: 992px)'])
                .pipe(
                    tap((value: BreakpointState) => {
                        if (value.matches) {
                            this.filterComponentProps = {
                                searchPlaceHolder: this.translateService.instant('searchItems'),
                                dateLabel: this.translateService.instant('filterbyDate'),
                                startDateLabel: 'itemsoutStartDateFilter'
                            };
                        } else {
                            this.filterComponentProps = {
                                searchPlaceHolder: this.translateService.instant('Search contract#, Item Name, Item#, PO Number and Job Number'),
                                dateLabel: this.translateService.instant('selectDueDateRange'),
                                startDateLabel: 'itemsoutStartDateFilter'
                            };
                        }
                    }),
                    distinctUntilChanged()
                )
                .subscribe()
        );
        this.uiUrl = this.inputs?.uiUrl;

        this.subscriptions.push(
            this.appFacadeService.getContractDetail().subscribe(data => {
                if (data) {
                    this.appFacadeService.setSubActiveTab(SubComponentsToLoad.ContractDetail);
                }
            })
        );
    }

    openCallOff(id: string) {
        this.modal.close(id + '-view');
        this.modal.open(id);
    }

    autoSizeAll() {
        this.columnApi?.autoSizeAllColumns();
        this.gridApi?.sizeColumnsToFit();
    }

    public isRowSelectable: IsRowSelectable = (params: RowNode<ItemOutCP>) => {
        let isSelactable = !!params.data && params.data.lineItemStatus === LineItemStatus.Out;
        if (params.data) {
            this.subscriptions.push(
                this.appFacadeService.isLineItemCalledOff(params.data.id).subscribe(isCalledOff => {
                    isSelactable = isSelactable && !isCalledOff;
                })
            );
        }

        return isSelactable;
    };

    getSelectedRowData() {
        if (this.selectionDisabled) {
            return;
        }
        if (!this.isFullWidth) {
            this.selectedRows = this.gridApi.getSelectedRows();
        }
        this.modalItems = this.selectedRows;
        this.openedFromSelection = true;
        this.requestInitialView = this.resolvePermittedInitialView(RequestActionsToLoad.RequestReturnView);
        this.openRentalRequestModal();
    }

    /**
     * Open the Service request modal directly for a single item (from the row Actions kebab menu).
     * Uses modalItems only — must not touch the bulk selection / Off-Rent button state.
     */
    loadServiceRequestModal(item: ItemOutCP) {
        this.modalItems = [item];
        this.openedFromSelection = false;
        this.requestInitialView = this.resolvePermittedInitialView(RequestActionsToLoad.RequestServiceView);
        this.openRentalRequestModal();
    }

    /**
     * A direct-to-view shortcut must satisfy the same gates as the DefaultView button it skips:
     * the per-user account feature AND the org-level ability. When the target view is not
     * permitted, fall back to the DefaultView middle step so its own gating decides what shows.
     */
    private resolvePermittedInitialView(view: RequestActionsToLoad.RequestReturnView | RequestActionsToLoad.RequestServiceView): RequestActionsToLoad {
        const [accountFeature, orgFeature] =
            view === RequestActionsToLoad.RequestReturnView ? [UserAccountFeature.RequestCallOff, 'callOffAbility'] : [UserAccountFeature.RequestService, 'requestService'];

        return this.featureService.isAvailable(accountFeature) && this.featureService.isAvailable(orgFeature) ? view : RequestActionsToLoad.DefaultView;
    }

    onSelectionChanged() {
        if (this.enforcingSelectionCap) {
            return;
        }
        const selectedNodes = this.gridApi.getSelectedNodes();
        if (selectedNodes.length > this.maxItemsSelectable) {
            // Keep the first `maxItemsSelectable` and drop the rest — this also covers the header
            // select-all, which would otherwise select every loaded row.
            this.enforcingSelectionCap = true;
            selectedNodes.slice(this.maxItemsSelectable).forEach(node => node.setSelected(false));
            this.enforcingSelectionCap = false;
            this.notification.error(this.translateService.instant('MaximumOffRentItemsSelected', { max: this.maxItemsSelectable }));
        }
        this.selectionDisabled = true;
        this.totalRowsSelected = this.gridApi.getSelectedRows().length;
        if (this.totalRowsSelected > 0) {
            this.selectionDisabled = false;
        }
    }

    setResponsiveGrid(width: number) {
        this.gridReady$.next(false);
        this.selectedRows = [];
        this.totalRowsSelected = 0;
        this.selectionDisabled = true;

        if (width > 1024) {
            this.columnDefs = [
                // The Select boxes exist solely to feed the Off-Rent button, so they follow its gate.
                ...(this.isOffRentAvailable
                    ? [
                          {
                              field: 'select',
                              headerName: this.translateService.instant('select'),
                              minWidth: 90,
                              width: 90,
                              cellClass: 'theme-table-row align-center',
                              headerClass: 'theme-table-header align-center',
                              headerCheckboxSelection: true,
                              checkboxSelection: true,
                              cellStyle: { textAlign: 'center' }
                          }
                      ]
                    : []),
                {
                    field: this.featureService.isSyrinx() ? 'contractName' : 'contractId',
                    minWidth: 130,
                    headerName: this.translateService.instant('contract'),
                    cellRenderer: ContractDetailRendererComponent,
                    width: 130,
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header'
                },
                {
                    field: 'quantity',
                    headerName: this.translateService.instant('Quantity'),
                    width: 65,
                    minWidth: 65,
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header'
                },
                {
                    field: 'itemName',
                    headerName: this.translateService.instant('itemName'),
                    cellRenderer: ItemsOutDetailRendererComponent,
                    width: 350,
                    minWidth: 350,
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header',
                    cellStyle: { textAlign: 'left' }
                },
                {
                    field: 'Item #',
                    headerName: this.translateService.instant('Item#'),
                    valueGetter: (params: ValueGetterParams) => {
                        return this.itemOutService.getLineItemKeyName(params.data);
                    },
                    width: 165,
                    minWidth: 165,
                    autoHeight: true,
                    wrapText: true,
                    cellClass: 'theme-table-row item-number-cell',
                    headerClass: 'theme-table-header'
                },
                {
                    field: 'startDate',
                    headerName: this.translateService.instant('startDate'),
                    cellRenderer: this.formatDate,
                    width: 150,
                    minWidth: 150,
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header'
                },
                {
                    field: 'status',
                    headerName: this.translateService.instant('status'),
                    width: 120,
                    minWidth: 120,
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header'
                },
                ...(!this.featureService.isSyrinx()
                    ? [
                          {
                              field: 'customerPurchaseOrder',
                              headerName: this.translateService.instant('poNumber'),
                              width: 150,
                              minWidth: 150,
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(!this.featureService.isSyrinx()
                    ? [
                          {
                              field: 'customerJobNumber',
                              headerName: this.translateService.instant('jobNr'),
                              width: 150,
                              minWidth: 150,
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                {
                    field: 'requestDateTime',
                    headerName: this.translateService.instant('requests'),
                    cellRenderer: RequestCellRendererComponent,
                    width: 130,
                    minWidth: 130,
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header',
                    autoHeight: true
                },
                {
                    field: 'info',
                    headerName: this.translateService.instant('Depot'),
                    width: 110,
                    minWidth: 110,
                    autoHeight: true,
                    wrapText: true,
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header'
                }
            ];
            if (this.featureService.isAvailable('canSeeDeliveryAddressColumn')) {
                this.columnDefs.push({
                    field: 'address',
                    headerName: this.translateService.instant('location'),
                    width: 210,
                    minWidth: 210,
                    autoHeight: true,
                    wrapText: true,
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header'
                });
            }

            // Mirrors the Request Service button in the modal's DefaultView: per-user account
            // feature AND org-level ability. Per-row eligibility lives in the renderer.
            if (this.featureService.isAvailable(UserAccountFeature.RequestService) && this.featureService.isAvailable('requestService')) {
                this.columnDefs.push({
                    field: 'actions',
                    headerName: this.translateService.instant('actions'),
                    cellRenderer: ItemsOutActionsMenuComponent,
                    width: 100,
                    minWidth: 100,
                    pinned: 'right',
                    sortable: false,
                    cellClass: 'theme-table-row align-center actions-cell',
                    headerClass: 'theme-table-header align-center'
                });
            }

            this.headerHeight = 35;
            this.isFullWidth = false;
            this.rowHeight = 65;
        }
        if (width < 1024) {
            this.columnDefs = [];
            this.headerHeight = 0;
            this.isFullWidth = true;
            this.rowHeight = 360;
        }
        this.gridReady$.next(true);
    }
    onClosingRequestModal() {
        this.isRequestModalOpen = false;
        // Only clear the selection the modal was opened from; a per-item action (row kebab)
        // must leave whatever the user had checked in place.
        if (!this.isFullWidth && this.openedFromSelection) {
            this.onSelectionChanged();
            if (this.totalRowsSelected === 1) {
                this.gridApi.deselectAll();
            }
        }
        this.openedFromSelection = false;
        /**
         * Reset default view whenever rental request modal closes
         */
        this.requestActionService.setActiveTab(RequestActionsToLoad.DefaultView);
    }

    loadMore() {
        if (!this.customerId) {
            this.notification.error(this.translateService.instant('customerIdNotConfigured'));
            return;
        }
        this.itemOutService.getItemsOut({
            customerId: this.customerId,
            status: this.filters?.singleFilter,
            startDate: this.filters?.dates?.startDate,
            endDate: this.filters?.dates?.endDate,
            search: this.filters?.search
        });
    }

    exportCSV() {
        const visibleColumnKeys = this.columnApi
            .getAllDisplayedColumns()
            .map(column => column.getColId())
            // 'select' and 'actions' are renderer-only columns with no field on ItemOutCP,
            // so ag-grid would export them as empty cells.
            .filter(colId => colId !== 'select' && colId !== 'actions');
        const params: CsvExportParams = {
            skipHeader: false,
            skipGroups: true,
            fileName: 'Items-out.csv',
            columnKeys: visibleColumnKeys,
            processCellCallback: (param: ProcessCellForExportParams) => {
                return this.processCellCSV(param);
            }
        };
        this.gridApi.exportDataAsCsv(params);
    }

    processCellCSV(params: ProcessCellForExportParams): string {
        const colId = params.column.getColId();
        if (colId && ['startDate'].includes(colId)) {
            const depotId = params.node?.data?.depotId;

            // Get date only (MM/DD/YYYY)
            let dateOnlyStr = '';
            this.datePipe
                .transform(params.value, 'dateOnly', depotId)
                .subscribe(d => (dateOnlyStr = d))
                .unsubscribe();

            // Get timezone abbreviation from full format
            let fullStr = '';
            this.datePipe
                .transform(params.value, 'full', depotId)
                .subscribe(d => (fullStr = d))
                .unsubscribe();
            const timezoneMatch = fullStr.match(/\s([A-Z]{3,4})$/);
            const timezone = timezoneMatch ? timezoneMatch[1] : '';

            // Combine: "12/2/2025 CST"
            return timezone ? `${dateOnlyStr} ${timezone}` : dateOnlyStr;
        }
        if (colId && ['requestDateTime'].includes(colId)) {
            const depotId = params.node?.data?.depotId;
            let requestDate = '';
            this.datePipe
                .transform(params.value, 'full', depotId)
                .subscribe(d => (requestDate = d))
                .unsubscribe();
            let requestStr = this.translateService.instant('CallOff');
            if ((params.node?.data?.requestType as string) === (ReturnRequestType.Service as string)) {
                requestStr = this.translateService.instant('RequestService');
            }
            return requestDate ? `${requestStr} ${requestDate}` : '';
        }
        return params.value;
    }

    loadProductDetail(item: ItemOutCP) {
        this.appFacadeService.loadProductDetail(
            item.customerId,
            item.contractId,
            item.productId ?? '0',
            item.stockId ?? '0',
            ['Id', 'Name', 'SerialNumber', 'Images', 'Description'],
            () => {
                this.appFacadeService.setSubActiveTab(SubComponentsToLoad.ProductDetail);
            },
            item.id
        );
    }

    loadRequestmodal(item: ItemOutCP) {
        // Per-item action (mobile card "Requests"): open the modal for just this item without
        // engaging the bulk selection, so the Off-Rent button stays disabled after closing.
        this.modalItems = [item];
        this.openedFromSelection = false;
        this.requestInitialView = RequestActionsToLoad.DefaultView;
        this.openRentalRequestModal();
    }

    /**
     * @returns the checkbox state the caller should render — false when the tap was rejected for
     *          exceeding maxItemsSelectable, so the card's checkbox doesn't show a tick for an item
     *          that isn't in the selection.
     */
    setMobileCheckboxandItems(check: boolean, itemout: ItemOutCP): boolean {
        if (check && !this.selectedRows.some((item: ItemOutCP) => item.id === itemout.id) && this.selectedRows.length >= this.maxItemsSelectable) {
            this.notification.error(this.translateService.instant('MaximumOffRentItemsSelected', { max: this.maxItemsSelectable }));
            return false;
        }
        this.selectedRows = this.selectedRows.filter((item: ItemOutCP) => {
            return item.id !== itemout.id;
        });

        if (check) {
            this.selectedRows = [...this.selectedRows, itemout];
        }
        this.totalRowsSelected = this.selectedRows.length;
        this.selectionDisabled = this.selectedRows.length > 0 ? false : true;
        // Called from the ag-grid full-width row renderer (not a template-bound event), so OnPush
        // won't re-check this component automatically — mark it dirty so the button state updates.
        this.cdr.markForCheck();
        return check;
    }

    private openRentalRequestModal() {
        if (this.modalItems.length > 0) {
            // Rebuild the modal content fresh so it reflects the current items and requested view.
            this.isRequestModalOpen = true;
            this.modal.open('rental-request-page');
        }
    }

    onRequestModalCloseRequested() {
        this.modal.close('rental-request-page');
    }

    filterByStatus($e: string) {
        if ($e === this.allFilterValue) {
            // "All" shows every status: clear the client-side status filter.
            return (this.lineItemStatusFilter = undefined);
        }
        if ($e === LineItemStatus.OffRent) {
            return (this.lineItemStatusFilter = [LineItemStatus.OffRent, LineItemStatus.Returned]);
        }
        return (this.lineItemStatusFilter = $e);
    }

    filter($e: FilterOutput) {
        this.store.dispatch(clearItemsOut());
        const isAll = $e.singleFilter === this.allFilterValue;
        // "All" sends an empty status so the backend returns items of every status.
        const filterOutput: FilterOutput = isAll ? { ...$e, singleFilter: '' } : $e;
        this.filters = filterOutput;
        if (this.filters) {
            this.lineItemStatusFilter = isAll ? undefined : $e.singleFilter;
            setTimeout(() => {
                // Waiting for page number to update if(not used settimeout does not update instantly)
                this.itemOutService.filter(filterOutput, this.customerId);
            }, 300);
        }
    }

    public getRowId: GetRowIdFunc = (params: GetRowIdParams) => {
        return params.data.id;
    };

    handleRentalFormSubmission($event: CallOffRequest) {
        this.modal.close('rental-request-page');
        this.modalItems.map(row => {
            this.store.dispatch(updateRow({ nodeId: row.id, colId: 'requestDateTime', value: $event.requestDate }));
            this.store.dispatch(updateRow({ nodeId: row.id, colId: 'requestType', value: $event.requestType }));
            this.store.dispatch(updateRow({ nodeId: row.id, colId: 'callOffQty', value: this.itemOutService.callOffLineItemQty(row.id, $event) }));
            this.store.dispatch(updateRow({ nodeId: row.id, colId: 'actualQuantity', value: this.itemOutService.lineItemActualQty(row.id, $event) }));
        });
    }

    getHeaderTemplate() {
        return `<div class="ag-cell-label-container" role="presentation" data-testid="por-cp-items-out-header">
            <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button" aria-hidden="true"></span>
            <span ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button" aria-hidden="true"></span>
            <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span ref="eText" class="ag-header-cell-text"></span>
                <span ref="eFilter" class="ag-header-icon ag-header-label-icon ag-filter-icon" aria-hidden="true"></span>
                <span ref="eSortOrder" class="ag-header-icon ag-header-label-icon ag-sort-order" aria-hidden="true"></span>
                <span ref="eSortAsc" class="ag-header-icon ag-header-label-icon ag-sort-ascending-icon" aria-hidden="true"></span>
                <span ref="eSortDesc" class="ag-header-icon ag-header-label-icon ag-sort-descending-icon" aria-hidden="true"></span>
                <span ref="eSortNone" class="ag-header-icon ag-header-label-icon ag-sort-none-icon" aria-hidden="true"></span>
            </div>
        </div>`;
    }

    loadContractDetail(contractId: string) {
        this.appFacadeService.fetchContractDetail(false, this.customerId, contractId);
        /**
         * dispatch action to make contract select for contract detail
         */
        this.appFacadeService.openContract(contractId);
    }

    onClosePanelContractDetail() {
        this.appFacadeService.setSubActiveTab(SubComponentsToLoad.Default);
        /**
         * dispatch action to make contract unselected/reset for contract detail
         */
        this.appFacadeService.openContract('');
        /**
         * For payment we always needs latest data, so clear previous state data
         */
        this.appFacadeService.clearContractDetail();
    }

    onClosePanelProductDetail() {
        this.appFacadeService.setSubActiveTab(SubComponentsToLoad.Default);
        this.appFacadeService.resetProductDetail();
    }
}
